import React, { useState } from 'react';
import { FaTrophy, FaMedal, FaAward, FaCrown, FaChartBar } from 'react-icons/fa';
import styles from './AwardLeaderboard.module.css';

const AwardLeaderboard = ({ statistics }) => {
  const [sortBy, setSortBy] = useState('totalAwards');
  const [sortOrder, setSortOrder] = useState('desc');

  if (!statistics || statistics.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <FaTrophy className={styles.emptyIcon} />
          <p>No award statistics available</p>
        </div>
      </div>
    );
  }

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <FaCrown style={{ color: '#FFD700' }} />;
      case 2: return <FaMedal style={{ color: '#C0C0C0' }} />;
      case 3: return <FaAward style={{ color: '#CD7F32' }} />;
      default: return <span className={styles.rankNumber}>{rank}</span>;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return '#666';
    }
  };

  const sortedStatistics = [...statistics].sort((a, b) => {
    const aValue = a[sortBy] || 0;
    const bValue = b[sortBy] || 0;
    
    if (sortOrder === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Awards Leaderboard</h2>
        <div className={styles.sortControls}>
          <span>Sort by:</span>
          <button
            className={`${styles.sortButton} ${sortBy === 'totalAwards' ? styles.active : ''}`}
            onClick={() => handleSort('totalAwards')}
          >
            Total Awards {getSortIcon('totalAwards')}
          </button>
          <button
            className={`${styles.sortButton} ${sortBy === 'awardsByType.individual' ? styles.active : ''}`}
            onClick={() => handleSort('awardsByType.individual')}
          >
            Individual {getSortIcon('awardsByType.individual')}
          </button>
          <button
            className={`${styles.sortButton} ${sortBy === 'awardsByType.statistical' ? styles.active : ''}`}
            onClick={() => handleSort('awardsByType.statistical')}
          >
            Statistical {getSortIcon('awardsByType.statistical')}
          </button>
          <button
            className={`${styles.sortButton} ${sortBy === 'awardsByType.character' ? styles.active : ''}`}
            onClick={() => handleSort('awardsByType.character')}
          >
            Character {getSortIcon('awardsByType.character')}
          </button>
        </div>
      </div>

      <div className={styles.leaderboard}>
        <div className={styles.tableHeader}>
          <div className={styles.rankColumn}>Rank</div>
          <div className={styles.playerColumn}>Player</div>
          <div className={styles.totalColumn}>Total</div>
          <div className={styles.individualColumn}>Individual</div>
          <div className={styles.statisticalColumn}>Statistical</div>
          <div className={styles.characterColumn}>Character</div>
          <div className={styles.positionColumn}>Position</div>
          <div className={styles.teamColumn}>Team</div>
        </div>

        <div className={styles.tableBody}>
          {sortedStatistics.map((stat, index) => {
            const rank = index + 1;
            const player = stat.userId;
            const totalAwards = stat.totalAwards || 0;
            const awardsByType = stat.awardsByType || {};
            
            return (
              <div
                key={stat._id}
                className={`${styles.tableRow} ${rank <= 3 ? styles.topThree : ''}`}
                style={{ borderLeftColor: getRankColor(rank) }}
              >
                <div className={styles.rankColumn}>
                  <div className={styles.rankContainer}>
                    {getRankIcon(rank)}
                  </div>
                </div>
                
                <div className={styles.playerColumn}>
                  <div className={styles.playerInfo}>
                    <div className={styles.playerName}>
                      {player.firstName} {player.lastName}
                    </div>
                    <div className={styles.playerDetails}>
                      {player.email}
                    </div>
                  </div>
                </div>
                
                <div className={styles.totalColumn}>
                  <div className={styles.awardCount}>
                    <FaTrophy className={styles.countIcon} />
                    <span>{totalAwards}</span>
                  </div>
                </div>
                
                <div className={styles.individualColumn}>
                  <div className={styles.awardCount}>
                    <span>{awardsByType.individual || 0}</span>
                  </div>
                </div>
                
                <div className={styles.statisticalColumn}>
                  <div className={styles.awardCount}>
                    <span>{awardsByType.statistical || 0}</span>
                  </div>
                </div>
                
                <div className={styles.characterColumn}>
                  <div className={styles.awardCount}>
                    <span>{awardsByType.character || 0}</span>
                  </div>
                </div>
                
                <div className={styles.positionColumn}>
                  <div className={styles.awardCount}>
                    <span>{awardsByType.position || 0}</span>
                  </div>
                </div>
                
                <div className={styles.teamColumn}>
                  <div className={styles.awardCount}>
                    <span>{awardsByType.team || 0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Stats */}
      <div className={styles.summaryStats}>
        <div className={styles.summaryCard}>
          <FaChartBar className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <h3>{statistics.length}</h3>
            <p>Total Players</p>
          </div>
        </div>
        
        <div className={styles.summaryCard}>
          <FaTrophy className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <h3>
              {statistics.reduce((sum, stat) => sum + (stat.totalAwards || 0), 0)}
            </h3>
            <p>Total Awards Given</p>
          </div>
        </div>
        
        <div className={styles.summaryCard}>
          <FaMedal className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <h3>
              {statistics.length > 0 
                ? Math.round(statistics.reduce((sum, stat) => sum + (stat.totalAwards || 0), 0) / statistics.length * 10) / 10
                : 0
              }
            </h3>
            <p>Average per Player</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AwardLeaderboard;
